import axios from "axios";
import get from "lodash/get";
import trim from "lodash/trim";
import random from "lodash/random";
import ApiError from "../exceptions/apiError";
import validate from "../core/validate";
import { getSpeechRecognitionEndpointUrl, getSpeechRecognitionKey } from "../config";

/**
 * get the speech recognition score
 *
 * @param {!Object} payload - The object with all the params
 * @param {!string} payload.id - the user id
 * @param {!boolean} payload.isDemoStudent - if student is flaged as demo
 * @param {!boolean} payload.isStagingEnvironment - if the env is diff from production
 * @param {!boolean} payload.allowSpeechRecognitionBonus - allow the SR bonus
 * @param {!blob} payload.record - the user record blob
 * @param {!string} payload.text - the item text
 * @param {!Object} payload.passUnknownWordsError - send true to receive the raw error
 * @param {Object} payload.wordsDictionary - the object of word to replace
 *
 * @throws {ApiError} throws an exception with the api error
 *
 * @returns {Promise<*>}
 */
export const getScore = async (payload) => {
  validate(
    {
      id: {
        presence: {
          allowEmpty: false,
        },
      },
      isDemoStudent: {
        presence: {
          allowEmpty: false,
        },
      },
      isStagingEnvironment: {
        presence: {
          allowEmpty: false,
        },
      },
      record: {
        presence: {
          allowEmpty: false,
        },
      },
      text: {
        presence: {
          allowEmpty: false,
        },
      },
      wordsDictionary: {
        presence: true,
      },
      allowSpeechRecognitionBonus: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    payload
  );

  try {
    const formData = new FormData();

    formData.append("id", payload.id);
    formData.append("demoStudent", payload.isDemoStudent);
    formData.append("isStaging", payload.isStagingEnvironment);
    formData.append("audio", payload.record);
    formData.append("text", applyDictionaryToText(payload.text, payload.wordsDictionary));

    const { data: speechRecognitionResult } = await axios({
      headers: {
        "X-Api-Key": getSpeechRecognitionKey(),
        "Content-Type": "multipart/form-data",
      },
      url: getSpeechRecognitionEndpointUrl(),
      method: "POST",
      data: formData,
    });

    if (payload.allowSpeechRecognitionBonus && speechRecognitionResult.qualityScore < 70) {
      speechRecognitionResult.qualityScore += 20;
      speechRecognitionResult.wordScoreList = speechRecognitionResult.wordScoreList.map((wordScore) => ({
        ...wordScore,
        qualityScore: wordScore.qualityScore + 20 > 100 ? 100 : wordScore.qualityScore + 20,
      }));
    }

    // code to join words that has separated scores because of -
    speechRecognitionResult.wordScoreList = joinWordsSeparatedByChar(
      "-",
      payload.text,
      speechRecognitionResult.wordScoreList
    );

    speechRecognitionResult.text = payload.text;
    speechRecognitionResult.wordScoreList = applyDictionaryToWords(
      payload.text,
      payload.wordsDictionary,
      speechRecognitionResult.wordScoreList
    );

    return speechRecognitionResult;
  } catch (error) {
    if (error && get(error, "response.data.short_message", "") === "error_unknown_words") {
      if (payload.passUnknownWordsError) {
        throw new ApiError({
          response: {
            data: {
              message: "Invalid words in text",
              error: "error_unknown_words",
            },
          },
        });
      }
      return {
        text: payload.text,
        qualityScore: random(80, 90),
        wordScoreList: trim(payload.text)
          .split(" ")
          .map((slice) => ({
            word: slice,
            qualityScore: random(80, 90),
          })),
      };
    } else if (error && get(error, "response.data.short_message", "") === "error_no_speech") {
      throw new ApiError({
        response: {
          data: {
            message: "No speech was detected",
            error: "error_no_speech",
          },
        },
      });
    } else if (error && get(error, "response.data.short_message", "") === "error_word_alignment") {
      throw new ApiError({
        response: {
          data: {
            message: "You must record the complete phrase",
            error: "error_word_alignment",
          },
        },
      });
    } else {
      throw new ApiError({
        response: {
          data: {
            message: "Unexpected error to get score",
            error: "error_get_speech_recognition_score",
          },
        },
      });
    }
  }
};

const applyDictionaryToText = (text, dictionary) =>
  trim(text)
    .split(" ")
    .map((slice) => {
      let translatedValue = null;
      if (slice.split(".").length - 1 > 1) {
        translatedValue = get(
          dictionary,
          slice
            .replace(/[!?,]$/, "")
            .replace(/"/g, "")
            .replace(/[.]/g, "_"),
          undefined
        );
      } else {
        translatedValue = get(
          dictionary,
          slice
            .replace(/[!?,.]$/, "")
            .replace(/"/g, "")
            .replace(/[.]/g, "_"),
          undefined
        );
      }
      if (translatedValue) {
        return trim(translatedValue)
          .replace(/[' ']/g, "-")
          .concat(/[!.?,]$/.test(slice) ? slice.substring(slice.length - 1) : "");
      }
      return slice;
    })
    .reduce((acc, slice) => acc.concat(slice).concat(" "), "")
    .trim();

const applyDictionaryToWords = (text, dictionary, words) => {
  let availableWords = [...words];

  return trim(text)
    .split(" ")
    .map((slice) => {
      const translatedWords = get(dictionary, slice.replace(/[!?,.]$/g, "").replace(/[.]/g, "_"), undefined);
      if (translatedWords) {
        const foundAsOneWord = availableWords.find(
          (wordScore) => wordScore.word === translatedWords || wordScore.word === translatedWords.replace(/[' ']/g, "-")
        );
        if (foundAsOneWord) {
          availableWords = availableWords.slice(1);
          return {
            word: slice,
            qualityScore: foundAsOneWord.qualityScore,
            audioStartTime: foundAsOneWord.audioStartTime,
            audioEndTime: foundAsOneWord.audioEndTime,
          };
        }

        const translatedScores = trim(translatedWords)
          .replace(/[' ']/g, "-")
          .split("-")
          .map((word) => {
            const currentWordScore = availableWords.find((wordScore) => wordScore.word === word);
            availableWords = availableWords.slice(1);
            return currentWordScore;
          });
        return {
          word: slice,
          qualityScore:
            translatedScores.reduce((acc, wordScore) => acc + wordScore.qualityScore, 0) / translatedScores.length,
          audioStartTime: translatedScores[0].audioStartTime,
          audioEndTime: translatedScores[translatedScores.length - 1].audioEndTime,
        };
      }

      const currentWordScore = availableWords[0];
      availableWords = availableWords.slice(1);
      return currentWordScore;
    });
};

const joinWordsSeparatedByChar = (separatorChar, text, wordScoreList) => {
  text
    .split(" ")
    .map((slice) => slice.replace(/[!.?,]/g, ""))
    .filter((slice) => slice.indexOf(separatorChar) > -1)
    .forEach((slice) => {
      const words = slice.split(separatorChar);
      const initialWordIndex = wordScoreList.findIndex((wordScore, wordScoreIndex) =>
        words.reduce((acc, w, index) => acc && w === wordScoreList[wordScoreIndex + index].word, true)
      );

      if (initialWordIndex > -1) {
        const wordsToJoin = wordScoreList.slice(initialWordIndex, initialWordIndex + words.length);

        // eslint-disable-next-line no-param-reassign
        wordScoreList = [
          ...wordScoreList.slice(0, initialWordIndex),
          {
            word: slice,
            qualityScore: wordsToJoin.reduce((acc, wordScore) => acc + wordScore.qualityScore, 0) / wordsToJoin.length,
            audioStartTime: wordsToJoin[0].audioStartTime,
            audioEndTime: wordsToJoin[wordsToJoin.length - 1].audioEndTime,
          },
          ...wordScoreList.slice(initialWordIndex + words.length),
        ];
      }
    });

  return wordScoreList;
};
